import { useMemo } from 'react';
import { Icon, StatusDot } from '../chrome/Icon';
import { AvatarButton } from '../chrome/Avatar';
import { useBabyMilestones, useLeaps } from '../../data/queries';
import { rowToLeap, rowToMilestone } from '../../data/derive';
import { leapState, type LeapState } from '../shared/leapState';
import type { Baby, Leap, Milestone } from '../../data/types';
import { useT } from '../../i18n';

interface Props {
  baby: Baby;
  onOpenLeap: (id: number) => void;
  /** Legacy ignored. */
  accent?: string;
  accentInk?: string;
  onOpenProfile?: () => void;
  showHeader?: boolean;
}

interface LeapWithItems extends Leap {
  milestones: Milestone[];
}

export function Leaps({ baby, onOpenLeap, onOpenProfile, showHeader = true }: Props) {
  const t = useT();
  const leapsQ = useLeaps();
  const msQ    = useBabyMilestones(baby.id ?? null);

  const grouped: LeapWithItems[] = useMemo(() => {
    const leaps = (leapsQ.data ?? []).map(rowToLeap);
    const milestones = (msQ.data?.milestones ?? []).map(rowToMilestone);
    return leaps.map((l) => ({
      ...l,
      milestones: milestones.filter((m) => m.leap === l.id),
    }));
  }, [leapsQ.data, msQ.data]);

  // Loading on first paint — return a minimal skeleton instead of empty UI so
  // the layout doesn't flash.
  if (leapsQ.isLoading) {
    return (
      <div className="flex flex-col gap-3 pb-6">
        <div className="h-3 w-24 rounded-sm bg-grey-soft" />
        <div className="h-9 w-72 rounded bg-grey-soft" />
        <div className="h-44 rounded-2xl bg-grey-soft/60" />
        <div className="h-32 rounded-2xl bg-grey-soft/40" />
        <div className="h-32 rounded-2xl bg-grey-soft/40" />
      </div>
    );
  }

  const currentLeap =
    grouped.find((l) => leapState(l, baby.ageWeeks) === 'current') ??
    grouped.find((l) => leapState(l, baby.ageWeeks) === 'upcoming') ??
    grouped[grouped.length - 1];

  return (
    <div className="flex flex-col gap-5 pb-6">
      {/* Heading */}
      {showHeader && (
        <header className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">
              {t('leaps.title')}
            </p>
            <h1 className="mt-1 font-serif text-[28px] nav:text-3xl leading-tight tracking-tight text-ink whitespace-pre-line">
              {t('leaps.gatewayTitle')}
            </h1>
            <p className="mt-2.5 max-w-[280px] text-[13px] leading-snug text-ink-2 text-pretty">
              {t('leaps.gatewayDesc')}
            </p>
          </div>
          <div className="nav:hidden">
            {onOpenProfile && <AvatarButton size={40} baby={baby} onOpenProfile={onOpenProfile} />}
          </div>
        </header>
      )}

      {currentLeap && (
        <CurrentLeapCallout
          leap={currentLeap}
          babyAgeWeeks={baby.ageWeeks}
          onOpen={() => onOpenLeap(currentLeap.id)}
        />
      )}

      <section>
        <h2 className="mb-3.5 font-serif text-[22px] tracking-tight text-ink px-1">
          {t('leaps.section.all')}
        </h2>
        <div className="flex flex-col gap-3">
          {grouped.map((l) => (
            <LeapCard
              key={l.id}
              leap={l}
              state={leapState(l, baby.ageWeeks)}
              onOpen={() => onOpenLeap(l.id)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}

interface CalloutProps {
  leap: LeapWithItems;
  babyAgeWeeks: number;
  onOpen: () => void;
}

function CurrentLeapCallout({ leap, babyAgeWeeks, onOpen }: CalloutProps) {
  const t = useT();
  const done = leap.milestones.filter((m) => m.status === 'done').length;
  const total = leap.milestones.length;
  const delta = babyAgeWeeks - leap.week;
  const deltaLabel =
    delta === 0 ? t('leaps.callout.delta.now') :
    delta > 0   ? t('leaps.callout.delta.past').replace('{count}', String(delta)) :
                  t('leaps.callout.delta.future').replace('{count}', String(-delta));

  return (
    <button
      onClick={onOpen}
      className="relative block w-full overflow-hidden rounded-[18px] p-5 text-left"
      style={{
        background: 'color-mix(in oklch, var(--color-accent) 9%, var(--color-bg))',
        border: '1px solid color-mix(in oklch, var(--color-accent) 22%, transparent)',
      }}
    >
      <div
        className="pointer-events-none absolute -right-15 -top-15 size-50 rounded-full"
        style={{ border: '1.5px solid color-mix(in oklch, var(--color-accent) 30%, transparent)' }}
      />
      <div
        className="pointer-events-none absolute -right-5 -top-5 size-30 rounded-full"
        style={{ background: 'color-mix(in oklch, var(--color-accent) 14%, transparent)' }}
      />

      <p className="relative text-[11px] font-bold uppercase tracking-[0.14em] text-accent-ink">
        {t('leaps.callout.title').replace('{deltaLabel}', deltaLabel)}
      </p>
      <div className="relative mt-2.5 flex items-baseline gap-2.5">
        <span className="font-serif text-5xl leading-none tracking-[-0.04em] text-ink">{leap.id}</span>
        <span className="font-serif text-[22px] leading-tight tracking-tight text-ink">{leap.name}</span>
      </div>
      <p className="relative mt-2.5 max-w-[290px] text-[13.5px] leading-snug text-ink-2 text-pretty">
        {leap.desc}
      </p>
      <div className="relative mt-4 flex items-center justify-between">
        <span className="text-xs font-semibold tracking-wide text-ink-2">
          {t('leaps.callout.reached')
            .replace('{done}', String(done))
            .replace('{total}', String(total))
            .replace('{week}', String(leap.week))}
        </span>
        <span className="inline-flex items-center gap-1 text-xs font-semibold text-accent-ink">
          {t('leaps.callout.open')} <Icon name="arrow" size={14} color="var(--color-accent-ink)" />
        </span>
      </div>
    </button>
  );
}

interface CardProps {
  leap: LeapWithItems;
  state: LeapState;
  onOpen: () => void;
}

function LeapCard({ leap, state, onOpen }: CardProps) {
  const t = useT();
  const done = leap.milestones.filter((m) => m.status === 'done').length;
  const total = leap.milestones.length;

  const meta = {
    past:     { label: t('leaps.card.past'), pillBg: 'color-mix(in oklch, var(--color-accent) 14%, var(--color-bg))', pillColor: 'var(--color-accent-ink)', rail: 'bg-accent' },
    current:  { label: t('leaps.card.current'), pillBg: 'var(--color-amber-soft)',                                       pillColor: 'var(--color-amber-ink)',  rail: 'bg-amber'  },
    upcoming: { label: t('leaps.card.upcoming'), pillBg: 'var(--color-grey-soft)',                                        pillColor: 'var(--color-ink-2)',      rail: 'bg-grey'   },
  }[state];

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={onOpen}
      className="relative flex flex-col gap-2.5 rounded-2xl border border-line bg-surface pl-4.5 pr-4 pt-4 pb-3.5 cursor-pointer"
    >
      <div className={`absolute left-0 top-3.5 bottom-3.5 w-[3px] rounded-sm ${meta.rail}`} />

      <div className="flex items-start gap-3.5">
        <div className="w-11 shrink-0 text-right font-serif text-[34px] leading-none tracking-[-0.04em] text-ink">
          {leap.id}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <h3 className="font-serif text-lg leading-snug tracking-tight text-ink">{leap.name}</h3>
            <span
              className="shrink-0 whitespace-nowrap rounded-xl px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider"
              style={{ background: meta.pillBg, color: meta.pillColor }}
            >
              {meta.label}
            </span>
          </div>
          <p className="mt-1 text-[12.5px] leading-snug text-ink-2 text-pretty">{leap.desc}</p>
        </div>
      </div>

      <div className="ml-15 flex items-center gap-2.5 flex-wrap">
        <span className="text-[11px] font-semibold tracking-wide text-ink-3 tabular-nums">
          {t('leaps.card.peaks').replace('{week}', String(leap.week))}
        </span>
        <span className="size-0.75 rounded-full bg-ink-3" />
        <span className="text-[11px] font-semibold tracking-wide text-ink-3">
          {total === 0 ? t('leaps.card.none') : t('leaps.card.reached').replace('{done}', String(done)).replace('{total}', String(total))}
        </span>
      </div>

      {total > 0 && (
        <div className="ml-15 flex flex-wrap gap-1">
          {leap.milestones.map((m) => {
            const styles =
              m.status === 'done'
                ? { bg: 'color-mix(in oklch, var(--color-accent) 12%, var(--color-bg))', fg: 'var(--color-accent-ink)', dot: 'var(--color-accent)' }
                : m.status === 'now'
                ? { bg: 'var(--color-amber-soft)', fg: 'var(--color-amber-ink)', dot: 'var(--color-amber)' }
                : { bg: 'var(--color-grey-soft)', fg: 'var(--color-ink-2)',  dot: 'var(--color-grey)' };
            return (
              <span
                key={m.id}
                title={m.name}
                className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium"
                style={{ background: styles.bg, color: styles.fg }}
              >
                <StatusDot status={m.status} color="var(--color-accent)" size={12} />
                {m.name}
              </span>
            );
          })}
        </div>
      )}
    </article>
  );
}
